import axios from 'axios';

const BASE = process.env.REACT_APP_API_URL ?? '';

// Anonymous chat/share/escalate traffic — must never carry the auth cookie.
export const publicApi = axios.create({
  baseURL: BASE,
  withCredentials: false,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// Authenticated dashboard/login traffic — carries the session cookie.
export const api = axios.create({
  baseURL: BASE,
  withCredentials: true,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

// TEMP DEBUG — logs every failed request/response so a staging login
// failure shows the real status/body in the browser console instead of
// just the generic "Access denied" text the UI intentionally shows.
// Remove once the staging auth issue is root-caused.
const logAxiosError = (label: string) => (error: any) => {
  // eslint-disable-next-line no-console
  console.error(`[auth-debug] ${label} failed`, {
    url: error?.config?.baseURL ? `${error.config.baseURL}${error.config.url}` : error?.config?.url,
    method: error?.config?.method,
    status: error?.response?.status,
    responseData: error?.response?.data,
    withCredentials: error?.config?.withCredentials,
    message: error?.message,
    // Network-level failure (CORS block, DNS, connection refused) has no
    // error.response at all — that distinction is the main thing worth
    // seeing here.
    isNetworkError: error?.response === undefined,
  });
  return Promise.reject(error);
};

publicApi.interceptors.response.use((response) => response, logAxiosError('publicApi'));

let onUnauthorized: (() => void) | null = null;

export const setUnauthorizedHandler = (fn: (() => void) | null) => {
  onUnauthorized = fn;
};

api.interceptors.request.use((config) => {
  if (config.method && config.method.toLowerCase() !== 'get') {
    const csrf = document.cookie
      .split('; ')
      .find((row) => row.startsWith('__Host-mira_csrf='))
      ?.split('=')[1];
    if (csrf) {
      config.headers['X-CSRF-Token'] = csrf;
    }
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    logAxiosError('api')(error).catch(() => {});
    const status = error.response?.status;
    const url = error.config?.url ?? '';
    // /api/auth/me 401 is the normal "not signed in" answer (AuthContext's
    // bootstrap probe owns it, not this handler); /api/auth/logout 401 means
    // already gone. There's no /login page to guard against re-triggering
    // anymore — sign-in is a popup, opening it again is a harmless no-op.
    const isAuthProbe = url.includes('/api/auth/me') || url.includes('/api/auth/logout');
    if (status === 401 && !isAuthProbe) {
      onUnauthorized?.();
    }
    return Promise.reject(error);
  }
);
